import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useAuthStore } from '../stores/authStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';

export function SubscriptionPendingPage() {
  const { t } = useTranslation('common');
  const logout = useAuthStore((s) => s.logout);
  const fetchStatus = useSubscriptionStore((s) => s.fetchStatus);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(async () => {
      setIsRefreshing(true);
      try {
        await fetchStatus();
        setLastChecked(new Date());
      } finally {
        setIsRefreshing(false);
      }
    }, 30 * 1000);

    return () => clearInterval(intervalId);
  }, [fetchStatus]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchStatus();
      setLastChecked(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4, type: 'spring' }}
            className="mb-6"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-2xl">
              <svg
                className="w-8 h-8 text-amber-600 dark:text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </motion.div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('subscription.pendingTitle', 'Subscription Required')}
          </h1>

          {/* Message */}
          <p className="text-gray-500 dark:text-slate-400 mb-6">
            {t(
              'subscription.pendingMessage',
              'Your account is pending activation. Please contact your administrator or distributor to activate your subscription.'
            )}
          </p>

          {/* Contact info */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600 dark:text-slate-300">
              {t(
                'subscription.pendingHelp',
                'If you already paid, please wait while your license is being activated. You can also contact support for assistance.'
              )}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner size="sm" />
                  {t('subscription.checking', 'Checking...')}
                </span>
              ) : (
                t('subscription.refresh', 'Refresh Status')
              )}
            </Button>
            <p className="text-xs text-gray-400 dark:text-slate-500">
              {t('subscription.lastChecked', 'Last checked')}: {lastChecked.toLocaleTimeString()}
            </p>
            <Button
              variant="ghost"
              className="w-full"
              onClick={handleLogout}
            >
              {t('subscription.logout', 'Sign Out')}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
